import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { editToToday, keepDate, PAST_DATE_BOX, promptsPast, todayLocal } from "./DateHint";
import { Modal } from "./Modal";

const BOX_ATTR: keyof typeof PAST_DATE_BOX = "data-past-date";

function isPastDateBox(target: EventTarget | null): target is HTMLInputElement {
  return target instanceof HTMLInputElement && target.type === "date" && target.hasAttribute(BOX_ATTR);
}

/**
 * The past-date pop-up (punch list F170, Jeremy 30 Oct 2026), mounted once
 * for the whole app. It replaces the line that sat under every date box
 * (F141/F144): a date before today now stops the person with a question --
 * keep it ("Use date") or put today in and fix it ("Edit date").
 *
 * One listener on the document rather than code in each screen: every box
 * that sets a due or client date carries `PAST_DATE_BOX`, and each screen
 * keeps its own save exactly as it was.
 *
 * Three things keep it from nagging or looping:
 * - only a WHOLE date opens it (`isCompleteDate`), never a year half typed;
 * - the box keeps the focus while it is open (the buttons do not take it on
 *   mouse-down), so opening it fires no blur, and a screen that saves on
 *   blur (Plan all stages) saves once -- on "Use date", or later, with
 *   today's date, after "Edit date";
 * - the date just kept is remembered per box, so it does not ask again.
 */
export function PastDatePrompt() {
  const [box, setBox] = useState<HTMLInputElement | null>(null);
  const open = useRef<HTMLInputElement | null>(null);
  const shown = useRef(new WeakMap<HTMLInputElement, string>());
  const kept = useRef(new WeakMap<HTMLInputElement, string>());

  useEffect(() => {
    // What the box showed when it was first used: a date that was already
    // past is not questioned just because somebody clicked into it.
    const onFocus = (e: FocusEvent) => {
      if (isPastDateBox(e.target) && !shown.current.has(e.target)) shown.current.set(e.target, e.target.value);
    };
    const onInput = (e: Event) => {
      const el = e.target;
      if (!isPastDateBox(el) || open.current) return;
      if (promptsPast(el.value, shown.current.get(el) ?? null, kept.current.get(el) ?? null, todayLocal())) {
        open.current = el;
        setBox(el);
      }
    };
    document.addEventListener("focusin", onFocus, true);
    document.addEventListener("input", onInput, true);
    return () => {
      document.removeEventListener("focusin", onFocus, true);
      document.removeEventListener("input", onInput, true);
    };
  }, []);

  const close = () => {
    open.current = null;
    setBox(null);
  };

  // Escape, the backdrop and the close button keep the date, as "Use date".
  const onUseDate = useCallback(() => {
    const el = open.current;
    open.current = null;
    setBox(null);
    if (!el) return;
    kept.current.set(el, el.value);
    keepDate(el);
  }, []);

  // Escape answers this pop-up alone. Caught on the way DOWN, before the
  // pop-up it may sit over (Plan all stages) hears it and closes too.
  useEffect(() => {
    if (!box) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onUseDate();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [box, onUseDate]);

  if (!box) return null;
  return createPortal(
    <Modal title="That date has passed" onCancel={onUseDate}>
      <div
        className="row"
        style={{ justifyContent: "flex-end" }}
        // The date box keeps the focus while the buttons are pressed.
        onMouseDown={(e) => e.preventDefault()}
      >
        <button onClick={onUseDate}>Use date</button>
        <button
          className="primary"
          onClick={() => {
            const el = box;
            close();
            editToToday(el, todayLocal());
          }}
        >
          Edit date
        </button>
      </div>
    </Modal>,
    document.body,
  );
}
